/** One task's outcome, paired with the item that produced it. */
export type SettledResult<T, R> =
  | { item: T; status: 'fulfilled'; value: R }
  | { item: T; status: 'rejected'; reason: unknown };

/**
 * Run a task per item, at most `limit` at a time, and report every outcome.
 *
 * Deliberately not `Promise.all`: these tasks each write to the server, so one
 * rejection abandoning the rest mid-batch is the inconsistent state the caller
 * is trying to avoid. Every item runs, and the caller decides what a partial
 * failure means.
 */
export async function runWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<SettledResult<T, R>[]> {
  const results: SettledResult<T, R>[] = new Array(items.length);
  let cursor = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;

      try {
        results[index] = {
          item: items[index],
          status: 'fulfilled',
          value: await worker(items[index], index),
        };
      } catch (reason) {
        results[index] = { item: items[index], status: 'rejected', reason };
      }
    }
  });

  await Promise.all(runners);
  return results;
}
